import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import { api } from '../../../api';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';

/**
 * DonorSection â€“ attach / detach a single donor of a specific gender type.
 *
 * Props:
 *  procedureId  â€“ ID of the procedure
 *  donorType    â€“ 'M' (male donor for DS/DET) | 'F' (female donor for OD/DET)
 *  canSave      â€“ permission to attach
 *  canEdit      â€“ permission to detach
 */
function DonorSection({ procedureId, donorType, canSave, canEdit, onDonorChange }) {
  const [currentDonor, setCurrentDonor] = useState(null);
  // Stores the full contact object (name, external_code) after a search-and-attach
  const [currentDonorContact, setCurrentDonorContact] = useState(null);
  const [loadingDonor, setLoadingDonor] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const genderLabel = donorType === 'M' ? 'Male' : 'Female';
  const genderColor = donorType === 'M' ? '#1976d2' : '#e91e8c';
  const GenderIcon = donorType === 'M' ? MaleIcon : FemaleIcon;

  useEffect(() => {
    loadCurrentDonor();
  }, [procedureId, donorType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCurrentDonor = async () => {
    setLoadingDonor(true);
    try {
      const res = await api.ivf.getProcedureDonor(procedureId, donorType);
      // ApiClient returns response.data directly — backend wraps in { data: record }
      const record = res?.data;
      setCurrentDonor(record || null);
      // If the backend response includes joined contact details, populate the contact card too
      if (record?.contact) {
        setCurrentDonorContact(record.contact);
      }
      if (onDonorChange) onDonorChange(record ? record.irm_contacts_id : null, record?.contact || null);
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('Failed to load donor', err);
      }
      setCurrentDonor(null);
      if (onDonorChange) onDonorChange(null, null);
    } finally {
      setLoadingDonor(false);
    }
  };

  const handleSearch = async () => {
    if (!searchText.trim()) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await api.contacts.searchDonors({
        search_text: searchText.trim(),
        gender: donorType,
        page: 1,
        limit: 10,
      });
      setSearchResults(res?.data?.donors || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Search failed', text: err?.response?.data?.message || 'Could not search donors' });
    } finally {
      setSearching(false);
    }
  };

  const handleAttach = async (donor) => {
    setAttaching(true);
    try {
      const res = await api.ivf.attachDonorToProcedure(procedureId, donor.id, donorType);
      // ApiClient returns response.data directly — backend wraps record in { data: record }
      const record = res?.data || { irm_contacts_id: donor.id, attached_at: new Date().toISOString() };
      setCurrentDonor(record);
      setCurrentDonorContact(donor);
      if (onDonorChange) onDonorChange(donor.id, donor);
      setSearchResults([]);
      setSearchText('');
      Swal.fire({ icon: 'success', title: 'Attached!', text: `${donor.first_name} attached as ${genderLabel} donor.`, timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to attach donor' });
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = async () => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Detach Donor?',
      text: `Remove the ${genderLabel} donor from this procedure?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, detach',
      cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    try {
      await api.ivf.detachDonorFromProcedure(procedureId, donorType);
      setCurrentDonor(null);
      setCurrentDonorContact(null);
      if (onDonorChange) onDonorChange(null, null);
      Swal.fire({ icon: 'success', title: 'Detached!', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to detach donor' });
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  if (loadingDonor) {
    return <Box display="flex" alignItems="center" gap={1} py={2}><CircularProgress size={20} /><Typography variant="body2">Loading {genderLabel} donor infoâ€¦</Typography></Box>;
  }

  return (
    <Box>
      {/* Donor type badge */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <GenderIcon sx={{ color: genderColor, fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: genderColor }}>
          {genderLabel} Donor
        </Typography>
      </Box>

      {/* Currently attached donor */}
      {currentDonor ? (
        <Card variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'success.light', bgcolor: '#f0fdf4' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: genderColor, width: 44, height: 44 }}>
                <PersonAddAlt1Icon />
              </Avatar>
              <Box>
                <Typography fontWeight="bold" variant="body1">
                  {currentDonorContact
                    ? [currentDonorContact.first_name, currentDonorContact.middle_name, currentDonorContact.last_name].filter(Boolean).join(' ')
                    : `${genderLabel} Donor`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentDonorContact ? currentDonorContact.external_code : `ID: ${currentDonor.irm_contacts_id}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Attached on: {currentDonor.attached_at ? new Date(currentDonor.attached_at).toLocaleDateString() : '–'}
                </Typography>
              </Box>
            </Box>
            {(canEdit || canSave) && (
              <Button
                variant="outlined" color="error" size="small"
                startIcon={<LinkOffIcon />}
                onClick={handleDetach}
              >
                Detach
              </Button>
            )}
          </Box>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>No {genderLabel.toLowerCase()} donor is currently attached to this procedure.</Alert>
      )}

      {/* Search & attach */}
      {(canSave || canEdit) && !currentDonor && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight="medium" mb={1}>Search &amp; Attach {genderLabel} Donor</Typography>
          <Box display="flex" gap={1} mb={2}>
            <TextField
              size="small" placeholder={`Search ${genderLabel.toLowerCase()} donor by name or codeâ€¦`}
              value={searchText} onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained" startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={handleSearch} disabled={searching} sx={{ color: '#fff', whiteSpace: 'nowrap', bgcolor: genderColor, '&:hover': { bgcolor: genderColor } }}
            >
              Search
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <Grid container spacing={1}>
              {searchResults.map((donor) => (
                <Grid item xs={12} sm={6} key={donor.id}>
                  <Card
                    variant="outlined"
                    sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                  >
                    <Avatar sx={{ bgcolor: genderColor, width: 36, height: 36, fontSize: 14 }}>
                      {(donor.first_name?.[0] || '') + (donor.last_name?.[0] || '')}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {[donor.first_name, donor.middle_name, donor.last_name].filter(Boolean).join(' ')}
                      </Typography>
                      <Box display="flex" gap={0.5}>
                        <Chip label={donor.external_code} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                        {donor.date_of_birth && <Chip label={`${calculateAge(donor.date_of_birth)} yrs`} size="small" variant="outlined" sx={{ fontSize: 10 }} />}
                      </Box>
                    </Box>
                    <Button
                      size="small" variant="contained" color="success"
                      onClick={() => handleAttach(donor)}
                      disabled={attaching}
                      sx={{ color: '#fff', fontSize: 11, whiteSpace: 'nowrap' }}
                    >
                      Attach
                    </Button>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {searchResults.length === 0 && searchText && !searching && (
            <Typography variant="body2" color="text.secondary">No {genderLabel.toLowerCase()} donors found for &ldquo;{searchText}&rdquo;</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default DonorSection;

DonorSection.propTypes = {
  procedureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  donorType: PropTypes.oneOf(['M', 'F']).isRequired,
  canSave: PropTypes.bool,
  canEdit: PropTypes.bool,
  onDonorChange: PropTypes.func,
};

DonorSection.defaultProps = {
  canSave: false,
  canEdit: false,
  onDonorChange: null,
};
